import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Card,
  CardActionArea,
  Checkbox,
  FormControlLabel,
  Radio,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import {
  adviseOnSenderValue,
  normalizeSenderValue,
  validateSenderValue,
} from "encamp-shared/src/emailIntake";
import { gql } from "generated-graphql";
import { EmailIntakeSenderMatchType } from "generated-graphql/graphql";
import { useEffect, useState } from "react";

import type { FacilityHint, SenderDialogValues } from "./senderDialogValues";

import { ConflictWarning } from "./ConflictWarning";
import { FacilityHintPicker } from "./FacilityHintPicker";
import { OrganizationPicker } from "./OrganizationPicker";

const CONFLICTS_QUERY = gql(`
  query EmailIntakeSenderConflicts(
    $tenantId: ID!
    $matchType: EmailIntakeSenderMatchType!
    $value: String!
    $includeSubdomains: Boolean
  ) {
    emailIntakeSenderConflicts(
      tenantId: $tenantId
      matchType: $matchType
      value: $value
      includeSubdomains: $includeSubdomains
    ) {
      tenantId
      tenantName
      enabled
      kind
      value
    }
  }
`);

const UPSERT_SENDER = gql(`
  mutation UpsertEmailIntakeSender($input: UpsertEmailIntakeSenderInput!) {
    upsertEmailIntakeSender(input: $input) {
      id
    }
  }
`);

/**
 * One of the two ways to match a sender.
 *
 * A radio rather than a bare card: the cards carry a paragraph each, which made them
 * read as two blocks of guidance rather than a choice, and nothing on them said only
 * one could be picked. The tinted fill is what makes the selection visible at a
 * glance — the 2px border alone was easy to miss between two otherwise identical
 * cards.
 */
/**
 * Which other organizations already claim this value.
 *
 * Its own hook because the debounce, the query and the stale-response guard are one
 * concern — "is this value already taken, as far as we currently know" — and inlining
 * them put the dialog over the complexity limit.
 */
function useSenderConflicts({
  tenantId,
  matchType,
  normalized,
  includeSubdomains,
  valid,
}: {
  tenantId: string;
  matchType: EmailIntakeSenderMatchType;
  normalized: string;
  includeSubdomains: boolean;
  valid: boolean;
}) {
  // Debounced so the check runs on a value someone has finished typing rather than on
  // every keystroke — a partial domain is usually valid-looking, so the undebounced
  // version fired a request per character.
  const [settledValue, setSettledValue] = useState(normalized);
  useEffect(() => {
    const timer = setTimeout(() => setSettledValue(normalized), 400);
    return () => clearTimeout(timer);
  }, [normalized]);

  const { data } = useQuery(CONFLICTS_QUERY, {
    variables: { tenantId, matchType, value: settledValue, includeSubdomains },
    // A conflict only exists for a value that would actually save, and it is scoped to
    // the organization being saved into — so it cannot be checked before one is chosen.
    skip: !tenantId || !settledValue || !valid,
    fetchPolicy: "cache-and-network",
  });

  // Nothing is reported for a value the user has since edited, so a late response
  // cannot warn about a value that is no longer on screen.
  if (settledValue !== normalized) return [];
  return data?.emailIntakeSenderConflicts ?? [];
}

function MatchTypeCard({
  selected,
  title,
  description,
  onClick,
}: {
  selected: boolean;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        // The border is always 2px and only its colour changes. Growing it on
        // selection moved the taller card by 2px, which moved the whole dialog —
        // the flex row takes its height from whichever card is tallest.
        borderWidth: 2,
        borderColor: selected ? "primary.main" : "transparent",
        // Without this the transparent border shows the paper behind it and the
        // unselected card loses its outline entirely.
        boxShadow: (theme) => (selected ? "none" : `inset 0 0 0 1px ${theme.palette.divider}`),
        // 4% so it reads as a state rather than a highlight, and stays legible under
        // the caption text sitting on top of it.
        bgcolor: (theme) => (selected ? alpha(theme.palette.primary.main, 0.04) : undefined),
      }}
    >
      {/* The row sizes to the taller card, and the action area centres its content
          in whatever height it gets — which left the shorter card's title floating
          in the middle while the other sat at the top. */}
      <CardActionArea
        onClick={onClick}
        sx={{
          p: 1.5,
          height: "100%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="flex-start">
          <Radio
            checked={selected}
            size="small"
            sx={{ p: 0, mt: 0.25 }}
            // The card is the click target; the radio is along for the ride, so it
            // must not steal focus or announce itself as a second control.
            tabIndex={-1}
            inputProps={{ "aria-hidden": true }}
          />
          <Stack spacing={0.25}>
            <Typography variant="subtitle2">{title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

export function SenderDialog({
  initialValues,
  onClose,
  onSaved,
}: {
  /** `emptySenderValues()` to add, or a row's values to edit. */
  initialValues: SenderDialogValues;
  onClose: () => void;
  onSaved: () => void;
}) {
  const alerts = useAlerts();
  // The organization is part of the form now rather than ambient in the page. Fixed
  // once an entry exists: the mutation keys on (matchType, value, tenantId), so
  // changing it here would silently move the entry — and its history — to another
  // customer rather than editing this one. Removing and re-adding is the honest way
  // to do that, and it leaves a trail.
  const [organization, setOrganization] = useState(initialValues.organization ?? null);
  const isEditing = Boolean(initialValues.id);
  const tenantId = organization?.id ?? "";
  // Plain useState seeded from props: the caller gives this component a `key` tied to
  // the row being edited, so opening a different row remounts it with fresh state.
  // That keeps the reset out of an effect and off the render path.
  const [matchType, setMatchType] = useState(initialValues.matchType);
  const [value, setValue] = useState(initialValues.value);
  const [includeSubdomains, setIncludeSubdomains] = useState(initialValues.includeSubdomains);
  const [label, setLabel] = useState(initialValues.label);
  // Defaults on for a new entry: adding a sender you do not want to accept mail from
  // is not a thing anyone does.
  const [enabled, setEnabled] = useState(initialValues.enabled ?? true);
  const [facility, setFacility] = useState<FacilityHint | null>(initialValues.facility ?? null);
  const [upsertSender, { loading: saving }] = useMutation(UPSERT_SENDER);

  const isDomain = matchType === EmailIntakeSenderMatchType.Domain;
  const normalized = normalizeSenderValue(isDomain ? "DOMAIN" : "ADDRESS", value);
  const validationMessage = value.trim()
    ? validateSenderValue(isDomain ? "DOMAIN" : "ADDRESS", value)
    : null;

  // Worth saying, not worth blocking. Rendered next to the conflict callout because it
  // is the same kind of statement: here is what saving this would mean.
  const advisory = value.trim()
    ? adviseOnSenderValue(isDomain ? "DOMAIN" : "ADDRESS", value)
    : null;

  const conflicts = useSenderConflicts({
    tenantId,
    matchType,
    normalized,
    includeSubdomains,
    valid: !validationMessage,
  });
  const handleSave = async () => {
    try {
      await upsertSender({
        variables: {
          input: {
            id: initialValues.id,
            tenantId,
            matchType,
            value,
            includeSubdomains: isDomain ? includeSubdomains : false,
            enabled,
            label: label.trim() || null,
            facilityId: facility?.id ?? null,
          },
        },
      });
      alerts.success(initialValues.id ? "Allowed sender updated." : "Allowed sender added.");
      onSaved();
      onClose();
    } catch (err) {
      alerts.error(err instanceof Error ? err.message : "Couldn't save that sender.", err);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={initialValues.id ? "Edit Allowed Sender" : "Add Allowed Sender"}
      // Whose allowlist this is. Staff work across customers in adjacent tabs, and
      // adding a sender to the wrong one otherwise looks exactly like adding it to
      // the right one.
      // Only the consequence, once there is one to state. The instruction for the
      // picker is the picker's own helper text — up here it described a control
      // rather than the dialog.
      description={
        organization ? (
          <>
            Mail from this sender will be accepted for <strong>{organization.name}</strong>.
          </>
        ) : undefined
      }
      primaryActionButtonProps={{
        saveText: initialValues.id ? "Save" : "Add sender",
        onClick: handleSave,
        loading: saving,
        disabled: saving || !organization || !value.trim() || Boolean(validationMessage),
      }}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <OrganizationPicker
          value={organization}
          onChange={setOrganization}
          disabled={isEditing}
          helperText={
            isEditing
              ? "An entry cannot be moved between organizations. Remove it and add it to the other one."
              : "Whose documents this sender's mail will land in."
          }
        />
        {/* No visible label and no required marker: one option is always selected, so
            there is nothing to require and nothing a heading would add that the two
            cards do not already say. The group is still named for screen readers. */}
        <Stack direction="row" spacing={1.5} role="radiogroup" aria-label="How to match">
          <MatchTypeCard
            selected={!isDomain}
            title="Email address"
            description="One mailbox. Recommended for vendors — a hauler serving several customers can hold a separate address per customer."
            onClick={() => setMatchType(EmailIntakeSenderMatchType.Address)}
          />
          <MatchTypeCard
            selected={isDomain}
            title="Domain"
            description="Every mailbox on the domain. Best for a customer's own staff."
            onClick={() => setMatchType(EmailIntakeSenderMatchType.Domain)}
          />
        </Stack>

        {/* The value and its subdomain scope are one decision — how broadly to match —
            so they group tightly instead of reading as two unrelated fields a full
            step apart. The always-present helper line doubles as their separator and
            keeps the checkbox from jumping when a validation message appears. */}
        <Stack spacing={0.5}>
          <TextField
            label={isDomain ? "Domain" : "Email address"}
            required
            // example.com is reserved for exactly this. Real vendor domains here read
            // as though they were already configured.
            placeholder={isDomain ? "vendor.example.com" : "dispatch@vendor.example.com"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={Boolean(validationMessage)}
            helperText={validationMessage ?? " "}
            fullWidth
            autoFocus
          />

          {/* Always present, disabled for an address rather than hidden or unmounted.
              Unmounting it grew the dialog by a row on the switch to Domain and moved
              every control below it, including the one just clicked; hiding it left an
              obviously empty gap instead. Disabled, the row keeps the height and says
              the option exists and does not apply here. */}
          <FormControlLabel
            disabled={!isDomain}
            control={
              <Checkbox
                checked={isDomain && includeSubdomains}
                disabled={!isDomain}
                onChange={(e) => setIncludeSubdomains(e.target.checked)}
              />
            }
            label="Also accept subdomains"
          />
        </Stack>

        {/* Also available as a switch in the grid, which is the fast path. Here so a
            change can be made deliberately alongside everything else, and so the
            dialog is not silently missing a field the row has. */}
        <FormControlLabel
          control={
            <Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
          }
          label={
            enabled
              ? "Enabled — accepting mail from this sender"
              : "Disabled — mail from this sender is refused"
          }
        />

        <TextField
          label="Label"
          placeholder="Dispatch desk — Dallas"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          fullWidth
        />

        {/* Facilities are per-organization, so there is nothing to search until one
            is chosen. */}
        {organization && (
          <FacilityHintPicker tenantId={tenantId} value={facility} onChange={setFacility} />
        )}

        {advisory && <Alert severity="warning">{advisory}</Alert>}

        <ConflictWarning conflicts={conflicts} />
      </Stack>
    </Dialog>
  );
}
